import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Feed } from "../types";
import { ACCENT, surface, text } from "../theme";
import { SourceFilter } from "./SourceFilter";
import { EmbedBridge } from "../hooks/useEmbedBridge";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  feeds: Feed[];
  selected: string[];
  onSelectionChange: (feedIds: string[]) => void;
  bridge: EmbedBridge;
}

/**
 * Search, refresh and the source filter, pinned to the top of the viewport.
 *
 * Standalone this is plain `position: sticky`. Embedded it cannot be: the host
 * sizes the iframe to the reader's full height, so the iframe never scrolls and
 * sticky never activates. There the bar is switched to `position: fixed` with
 * its `top` driven by the host's reported scroll position, and a spacer holds
 * its place in the flow so the grid does not jump when it pins.
 *
 * The host also reports how much of its viewport its own fixed furniture covers
 * (thetezos.com has a sticky site header), and the bar pins below that — at the
 * plain viewport top it would sit behind the host's header.
 */
export function ControlBar({
  search,
  onSearchChange,
  onRefresh,
  isRefreshing,
  feeds,
  selected,
  onSelectionChange,
  bridge,
}: Props) {
  const barRef = useRef<HTMLDivElement>(null);
  const [naturalTop, setNaturalTop] = useState(0);
  const [barHeight, setBarHeight] = useState(0);

  const { embedded, hostAware, visibleTop, topInset } = bridge;
  // Where the top of the actually-visible area falls in this document. Embedded
  // in a page with its own sticky header, that is below the host's viewport top.
  const visibleStart = visibleTop + topInset;
  // The bridge reports scroll position in both modes, so one condition covers
  // "the bar would have scrolled out of view by now".
  const stuck = barHeight > 0 && visibleStart > naturalTop;
  // Only the embedded case needs the position driven by hand.
  const pinned = embedded && hostAware && stuck;

  // Measuring must not happen while the bar is displaced, or it records the
  // pinned position as the natural one and never unpins.
  const displaced = useRef(false);
  displaced.current = pinned || (!embedded && stuck);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const measure = () => {
      const rect = bar.getBoundingClientRect();
      setBarHeight(Math.round(rect.height));
      if (!displaced.current) {
        setNaturalTop(Math.round(rect.top + window.scrollY));
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    // Observers are throttled in a backgrounded frame; timers are not.
    const poll = setInterval(measure, 1000);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      clearInterval(poll);
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <>
      {/* Holds the bar's place in the flow only while it is manually pinned.
          A wrapper around a `position: sticky` element would confine it to that
          wrapper's own box, and it would never travel. */}
      {pinned && <Box sx={{ height: barHeight }} aria-hidden />}

      <Box
        ref={barRef}
        sx={{
          zIndex: 20,
          mb: 2.5,
          backgroundColor: surface.page,
          borderBottom: `1px solid ${stuck ? surface.border : "transparent"}`,
          transition: "border-color .15s ease",
          ...(pinned
            ? { position: "fixed", top: visibleStart, left: 0, right: 0 }
            : embedded
            ? { position: "static" }
            : { position: "sticky", top: 0 }),
        }}
      >
        <Container maxWidth="xl" sx={{ py: 1.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search stories…"
              size="small"
              type="search"
              inputProps={{ "aria-label": "Search stories" }}
              sx={{ flex: { xs: 1, sm: "0 0 auto" }, width: { sm: 280 }, minWidth: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: text.tertiary }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => onSearchChange("")}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box sx={{ flex: 1 }} />

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  aria-label="Refresh articles"
                  sx={{
                    border: `1px solid ${surface.border}`,
                    borderRadius: 2,
                    color: text.secondary,
                  }}
                >
                  {isRefreshing ? (
                    <CircularProgress size={18} sx={{ color: ACCENT }} />
                  ) : (
                    <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {feeds.length > 0 && (
            <SourceFilter
              feeds={feeds}
              selected={selected}
              onChange={onSelectionChange}
            />
          )}
        </Container>
      </Box>
    </>
  );
}
